import {Injectable, Injector} from '@angular/core';
import {WizardNestedFormService} from '../abstract/wizard-nested-form-service';
import {MethodsProvider} from '@providers/methods/methods.provider';
import {WizardFormEventsService} from '../wizard-form-events.service';
import {DataStorageService} from '@helpers/services/data-storage.service';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ModelsInterfaces} from '@namespaces/models.namespace';
import {distinctUntilChanged, map, take, takeUntil} from 'rxjs/operators';
import {WizardInterfaces} from '../../../wizard.namespace';
import {Subject} from 'rxjs';
import {EquipmentApiService} from './api/equipment-api.service';
import {timer} from 'rxjs';
import {ErrorLoggerService} from '@services/error-logger.service';


/**
 * Типы шагов оборудования
 */
enum EquipmentType {
    reference = 'reference',    // Образцы и эталоны
    equipment = 'equipment'     // Оборудование
}

interface EquipmentStepValue {
    id: number;
    _touched: boolean;
    _visible: boolean;
    valid: boolean;
    is_reference: boolean;
}

@Injectable()
export class EquipmentService extends WizardNestedFormService {

    public refreshValue$ = new Subject();

    /**
     * Шаги оборудования по методам
     */
    public equipmentFormArray: FormArray = new FormArray([]);

    /**
     * Шаги образцов и эталонов по методам
     */
    public referenceFormArray: FormArray = new FormArray([]);

    private methods: ModelsInterfaces.Method[] = [];

    constructor(
        private methodsProvider: MethodsProvider,
        private wizardFormEvents: WizardFormEventsService,
        private storage: DataStorageService<WizardInterfaces.EquipmentStorageData>,
        private fb: FormBuilder,
        private api: EquipmentApiService,
        private logger: ErrorLoggerService,
    ) {
        super();

        this.methods = this.methodsProvider.data$.getValue().asRoot;

        /**
         * Предзаполняем сторадж дефолтными значениями по всем методам для оборудовани и образцов
         */
        this.methods.forEach(method => {
            this.resetStorageDataForMethod(method.id);
        });
    }

    init() {
        /**
         * Пропускаем первые сколько-то милисекунд, чтобы после восстановления значений формы
         * для области аттестации успели сработать debounceTime триггеры и сразу при инициализации
         * у нас уже были реальные данные формы, а не дефолтные значения и почти сразу реальные, пришедие с debounceTime задержкой
         */
        timer(150).pipe(
            takeUntil(this.destroyed$),
        ).subscribe(() => {

            this.methods.forEach(method => {
                this.wizardFormEvents.attestationChange$.pipe(
                    takeUntil(this.destroyed$),
                    map((value) => value.find(v => v.key === method.short_title)),
                    distinctUntilChanged((old, current) => JSON.stringify(old) === JSON.stringify(current))
                ).subscribe((value: WizardInterfaces.AttestationValue|undefined) => {
                    if (value) {
                        this.updateData(value, method.id, false);
                        this.updateData(value, method.id, true);
                    } else {
                        /**
                         * Сбрасываем значения для метода
                         */
                        this.resetStorageDataForMethod(method.id);
                        this.setFormStatuses(method.id, true, {visible: false, valid: true});
                        this.setFormStatuses(method.id, false, {visible: false, valid: true});
                    }
                    this.setHasNotModeratedStatus();
                });
            });

        });
    }

    restoreValidators() {
        this.restoreSteps();
    }

    /**
     * Восстановить состояние шагов визарда
     */
    private restoreSteps() {

        /**
         * Задаем формы для оборудования и образцов
         */
        [EquipmentType.reference, EquipmentType.equipment].forEach(type => {
            const array: FormArray = type === EquipmentType.reference ? this.referenceFormArray : this.equipmentFormArray;
            const value = array.value as EquipmentStepValue[];
            const ids = value && value.length ? value.map(v => +v.id) : [];
            const methodIds = this.methods.map(m => +m.id);

            /**
             * Удаляем шаги для несуществующих методов, если они есть
             */
            ids.forEach(id => {
                if (!methodIds.includes(id)) {
                    array.removeAt(ids.indexOf(id));
                }
            });

            /**
             * Восстанавливаем валидаторы для групп
             */
            array.controls.forEach((group: FormGroup) => {
                if (!group.contains('valid')) {
                    group.setControl('valid', new FormControl(false));
                }

                group.get('valid').setValidators(Validators.requiredTrue);
            });

            /**
             * Восстанавливаем шаги для тех методов, для которых у нас еще вдруг нет шагов
             */
            this.methods.forEach(method => {
                if (!ids.includes(+method.id)) {
                    array.push(this.fb.group({
                        _touched: [false],
                        _visible: [false],
                        is_reference: [type === EquipmentType.reference],
                        valid: [false, Validators.requiredTrue],
                        id: method.id,
                    }));
                }
            });
        });
    }

    /**
     * Получить данные из стораджа для типа оборудования по методу
     * @param methodId
     * @param isReference
     */
    public getStorageData(methodId: number, isReference: boolean): WizardInterfaces.EquipmentStorageData {
        const type = isReference ? EquipmentType.reference : EquipmentType.equipment;
        return this.storage.get(`${type}_${methodId}`);
    }

    public updateValue(methodId: number, isReference: boolean): void {
        const storageData = this.getStorageData(methodId, isReference);
        const attestation = storageData.attestation;
        this.updateData(attestation, methodId, isReference);
    }

    private updateData(attestation: WizardInterfaces.AttestationValue, methodId: number, isReference: boolean): void {
        this.api.getGroups(attestation, methodId, isReference).pipe(
            takeUntil(this.destroyed$),
            take(1)
        ).subscribe((data) => {
            if (data && data.length) {
                this.handleDataReceive(attestation, methodId, isReference, data);
            } else {
                this.storeData({
                    groups: [],
                    method_id: methodId,
                    attestation: attestation,
                    has_not_moderated: false
                }, methodId, isReference);
                this.setFormStatuses(methodId, isReference, {visible: false, valid: true});
            }

            this.setHasNotModeratedStatus();

            this.refreshValue$.next();
        }, (error) => {
            this.storeData({
                groups: [],
                method_id: methodId,
                attestation: attestation,
                has_not_moderated: false
            }, methodId, isReference);

            this.setFormStatuses(methodId, isReference, {visible: false, valid: true});

            this.setHasNotModeratedStatus();

            this.refreshValue$.next();
        });
    }

    /**
     * Обработка полученияя данных для заданного типа оборудования и метода
     * @param attestation
     * @param methodId
     * @param isReference
     * @param data
     */
    private handleDataReceive(
        attestation: WizardInterfaces.AttestationValue,
        methodId: number,
        isReference: boolean,
        data: WizardInterfaces.ApparatusStepGroup[]
    ) {
        this.storeData({
            groups: data,
            method_id: methodId,
            attestation: attestation,
            has_not_moderated: data.some(item => item.equipment.some(eq => Boolean(!eq.apparatus.is_moderated)))
        }, methodId, isReference);

        const valid = data.filter(group => group.required).every(group => group.valid);

        this.setFormStatuses(methodId, isReference, {visible: true, valid: valid});
    }

    /**
     * Сохранить данные в сторадже
     * @param data
     * @param methodId
     * @param isReference
     */
    private storeData(data: WizardInterfaces.EquipmentStorageData, methodId: number, isReference: boolean) {
        const type = isReference ? EquipmentType.reference : EquipmentType.equipment;
        this.storage.set(`${type}_${methodId}`, data);
    }

    /**
     * Установить статусы для формы
     * @param methodId
     * @param isReference
     * @param statuses
     */
    private setFormStatuses(methodId: number, isReference: boolean, statuses: {visible: boolean, valid: boolean}) {
        const array = isReference ? this.referenceFormArray : this.equipmentFormArray;
        const group = array.controls.find((g) => +g.get('id').value === methodId);

        if (!group) {
            const errorMessage = `Не удалось найти шаг для ${isReference ? 'образцов' : 'оборудования'} по методу с ID=${methodId}`;
            this.logger.log(new Error(errorMessage));
            return;
        }

        group.get('_visible').patchValue(Boolean(statuses.visible));
        group.get('valid').patchValue(Boolean(statuses.valid));
        /**
         * Если шаг невидим, то снимаем статус, что с ним работали
         */
        if (!Boolean(statuses.visible)) {
            group.get('_touched').patchValue(false);
        }
    }

    private setHasNotModeratedStatus() {
        this.wizardFormEvents.setNotModerated(this.isHasNotModerated());
    }

    private isHasNotModerated(): boolean {
        const storageValues = this.storage.getAll();
        return storageValues.some(value => value.has_not_moderated);
    }

    /**
     * Сбросить данные стораджа для метода (для оборудования и образцов)
     * @param methodId
     */
    private resetStorageDataForMethod(methodId: number) {
        [EquipmentType.reference, EquipmentType.equipment].forEach(type => {
            this.storeData({
                method_id: methodId,
                attestation: null,
                groups: [],
                has_not_moderated: false,
            }, methodId, type === EquipmentType.reference);
        });
    }
}

